import { useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchMediaLibrary } from '../services/api'
import { approvedLibraryImages, mergeLibraryImages } from '../utils/aiSuggestions'

/** The website the advertiser library is harvested from: Campaign Setting first. */
export function libraryWebsite(configuration) {
  return configuration?.campaign?.advertiser?.website
    || configuration?.ad?.clickoutUrl
    || configuration?.website
    || ''
}

// Our own copy when we hold one (AD-132); otherwise the advertiser's picture through our proxy.
export function proxiedImageUrl(item) {
  if (!item?.url) return ''
  if (item.source === 'ai') return item.url
  if (item.storedUrl) return item.storedUrl
  return `/api/studio-editor-v2/media-library/fetch?url=${encodeURIComponent(item.url)}`
}

// Image controls open together share one harvest; a closed library keeps nothing, as before.
export function useMediaLibraryQuery({ website, logos, enabled }) {
  return useQuery({
    queryKey: ['studio-editor-v2', 'media-library', logos ? 'logos' : 'images', website],
    queryFn: ({ signal }) => fetchMediaLibrary(website, logos, signal),
    enabled: Boolean(enabled && website),
    retry: false,
    gcTime: 0,
    networkMode: 'always',
    refetchOnReconnect: false,
  })
}

/**
 * The "Advertiser Library" of an image control: the images (or logos) the AI run approved,
 * followed by those harvested live from the advertiser's website. Fetches only while the
 * library is shown; `reload()` is "Import … from Website".
 */
export function useAdvertiserLibrary({ configuration, assetSourceUrls, logos = false, active = true }) {
  const website = libraryWebsite(configuration)
  const approved = useMemo(
    () => approvedLibraryImages(configuration, assetSourceUrls, { logos }),
    [assetSourceUrls, configuration, logos],
  )

  const query = useMediaLibraryQuery({ website, logos, enabled: active })
  const { refetch } = query

  // Harvested markup occasionally yields template placeholders ("${…}") instead of URLs.
  // The ad's own library may show an upload not saved yet by its preview (AD-234).
  const images = mergeLibraryImages(approved, query.data || [])
    .filter((item) => (item.source === 'ai' && /^data:image\//i.test(item.url))
      || (/^https?:\/\//i.test(item.url) && !/\$\{|\{\{|%7B/i.test(item.url)))
  const loading = Boolean(website) && active && (query.isPending || query.isFetching)

  return {
    website,
    images,
    loading,
    error: query.isError && !query.isFetching ? query.error.message : null,
    reload: useCallback(() => {
      if (active && website) refetch()
    }, [active, refetch, website]),
  }
}
